/**
 * Order-deadline indicator. Shows the countdown to the cutoff for the selected
 * service week (blue info), or a closed state once it's passed. Renders nothing
 * when the school has no deadline policy provisioned.
 *
 * Two presentations:
 *   - default: a full-width `Alert` (title + description) for standalone use.
 *   - `compact`: a single small inline row (clock + one line), sized to sit
 *     inside the calendar card beneath the legend separator.
 */

import { Alert, AlertDescription, AlertTitle } from '@ordo/ui/alert'
import { cn } from '@ordo/ui/cn'
import { Clock } from 'lucide-react'
import type { ReactElement } from 'react'

import { type SchoolDeadlinePolicy, weekDeadlineStatus } from '@/lib/school-orders/deadline'

export function OrderDeadlineAlert({
  policy,
  weekMondayKey,
  compact = false,
}: {
  /** The order-deadline policy to count down against (school or chef-destination). */
  policy: SchoolDeadlinePolicy
  weekMondayKey: string
  /** Render the small inline row (for inside the calendar card) instead of the Alert. */
  compact?: boolean
}): ReactElement | null {
  const status = weekDeadlineStatus(policy, weekMondayKey)

  if (status.daysRemaining === null) {
    return null // no deadline policy provisioned
  }

  if (compact) {
    return (
      <div
        className={cn(
          'flex items-center justify-center gap-1.5 text-xs',
          status.closed ? 'text-amber-700 dark:text-amber-400' : 'text-blue-700 dark:text-blue-300',
        )}>
        <Clock className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
        {status.closed ? (
          <span className="font-medium">Ordering closed for this week</span>
        ) : (
          <span>
            <span className="font-semibold">
              {status.daysRemaining} {status.daysRemaining === 1 ? 'day' : 'days'}
            </span>{' '}
            left to order
          </span>
        )}
      </div>
    )
  }

  if (status.closed) {
    return (
      <Alert variant="warning">
        <div className="flex items-start gap-2">
          <Clock className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <AlertTitle>Ordering closed</AlertTitle>
            <AlertDescription>The deadline to order for this week has passed.</AlertDescription>
          </div>
        </div>
      </Alert>
    )
  }

  return (
    <Alert variant="info">
      <div className="flex items-start gap-2">
        <Clock className="mt-0.5 h-4 w-4 shrink-0" />
        <div>
          <AlertTitle>
            {status.daysRemaining} {status.daysRemaining === 1 ? 'day' : 'days'} remaining
          </AlertTitle>
          <AlertDescription>To place orders for this week.</AlertDescription>
        </div>
      </div>
    </Alert>
  )
}
